import { quantidadeDe } from './lerLista';

/**
 * O parser da transferência entre localizadores em lote: texto colado ou
 * arquivo .csv/.txt → linhas com produto, localizadores e quantidade.
 *
 * O produto é o código, o EAN ou o lote; os localizadores são o CÓDIGO (que
 * não se repete — a descrição repete); a quantidade é número. Nada disso tem
 * espaço, então qualquer separador serve. Três formas, pelo número de colunas:
 *
 *   código                           → 1 un, origem e destino do topo (bipe)
 *   código ; qtde                    → origem e destino do topo
 *   código ; origem ; destino ; qtde → cada linha com os seus
 *
 * Com TAB (Excel) ou `;` a célula vazia conta: `12745;;41533;3` usa a origem
 * do topo e o destino da linha.
 */

export interface LinhaLote {
  linha: number;
  texto: string;
  codigo: string | null;
  /** `null` = usar o padrão do topo. */
  origem: string | null;
  destino: string | null;
  quantidade: number | null;
  problema: string | null;
  cabecalho: boolean;
}

export const MAX_LINHAS_LOTE = 2000;

export const MODELO_CSV =
  'produto;localizador_origem;localizador_destino;quantidade\r\n' +
  '12745;52109;41533;3\r\n' +
  '7897016833776;52109;45169;10\r\n';

function celulas(t: string): string[] {
  // TAB e `;` separam célula — e aí célula vazia significa "usa o padrão".
  if (t.includes('\t')) return t.split('\t').map((c) => c.trim().replace(/^"|"$/g, ''));
  if (t.includes(';')) return t.split(';').map((c) => c.trim().replace(/^"|"$/g, ''));
  // Senão, qualquer espaço, vírgula ou barra. "1.000" segue inteiro (é mil).
  return t
    .split(/[\s,|]+/)
    .map((c) => c.replace(/^"|"$/g, ''))
    .filter((c) => c !== '');
}

export function lerLote(texto: string): LinhaLote[] {
  const lidas: LinhaLote[] = [];
  let primeira = true;
  texto.split(/\r?\n/).forEach((bruta, i) => {
    const t = bruta.trim();
    if (t === '' || /^[;\t,\s]+$/.test(t)) return;
    const ehPrimeira = primeira;
    primeira = false;
    const base = { linha: i + 1, texto: t, origem: null, destino: null, quantidade: null };
    const falha = (codigo: string | null, problema: string): void => {
      lidas.push({ ...base, codigo, problema, cabecalho: false });
    };

    if (/^"?\d+[.,]\d+e\+?\d+/i.test(t)) {
      falha(null, 'EAN em notação científica — formate a coluna como texto no Excel');
      return;
    }

    // Planilha costuma vir com colunas a mais vazias no fim.
    const c = celulas(t);
    while (c.length > 1 && c[c.length - 1] === '') c.pop();

    // Produto: código interno, EAN ou o lote (alfanumérico, `V0000000129178`) —
    // o mesmo que o bipe do formulário aceita. Primeira linha cujo código não
    // tem dígito nenhum é o cabeçalho da planilha ("produto", "EAN").
    const codigo = c[0] ?? '';
    if (ehPrimeira && !/\d/.test(codigo)) {
      lidas.push({ ...base, codigo: null, problema: null, cabecalho: true });
      return;
    }
    if (!/^[0-9A-Za-z]{1,40}$/.test(codigo)) {
      falha(null, 'a linha não começa com o código, EAN ou lote do produto');
      return;
    }

    let origem: string | null = null;
    let destino: string | null = null;
    let qtde: string | undefined;
    if (c.length === 1) qtde = undefined;
    else if (c.length === 2) qtde = c[1];
    else if (c.length === 4) {
      origem = c[1] || null;
      destino = c[2] || null;
      qtde = c[3];
    } else {
      falha(
        codigo,
        `${c.length} colunas — use "código;qtde" ou "código;origem;destino;qtde"`,
      );
      return;
    }

    for (const [rotulo, v] of [
      ['origem', origem],
      ['destino', destino],
    ] as const) {
      if (v !== null && !/^\d{1,15}$/.test(v)) {
        falha(codigo, `${rotulo} "${v}" não é código de localizador`);
        return;
      }
    }

    const quantidade = qtde === undefined || qtde === '' ? 1 : quantidadeDe(qtde);
    if (!Number.isInteger(quantidade) || quantidade <= 0) {
      falha(codigo, `quantidade inválida (${qtde})`);
      return;
    }
    lidas.push({ ...base, codigo, origem, destino, quantidade, problema: null, cabecalho: false });
  });
  return lidas;
}
